import { useState, useEffect, useMemo, useCallback } from 'react';
import { COMPARISON_OPERATORS, useQuestionSectionMap } from '@/features/quote-and-apply-builder';

interface UseSectionSettingsProps {
  section: PolicyFormSection;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormSection>) => void;
  onClose: () => void;
}

export function useSectionSettings({
  section,
  questions,
  onSave,
  onClose,
}: UseSectionSettingsProps) {
  const questionSectionMap = useQuestionSectionMap();
  const [title, setTitle] = useState(section.title);
  const [hasCondition, setHasCondition] = useState(!!section.conditional?.visible);
  const [conditionType, setConditionType] = useState<'simple' | 'logical'>(
    section.conditional?.visible && 'conditions' in section.conditional.visible
      ? 'logical'
      : 'simple',
  );
  const [simpleCondition, setSimpleCondition] = useState<FieldCondition>(() => {
    const visible = section.conditional?.visible;
    if (visible && !('conditions' in visible)) {
      return visible as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [logicalCondition, setLogicalCondition] = useState<LogicalCondition>(() => {
    const visible = section.conditional?.visible;
    if (visible && 'conditions' in visible) {
      return visible as LogicalCondition;
    }
    return { operator: 'and', conditions: [] };
  });

  useEffect(() => {
    setTitle(section.title);
    setHasCondition(!!section.conditional?.visible);
    const visible = section.conditional?.visible;
    if (visible) {
      if ('conditions' in visible) {
        setConditionType('logical');
        setLogicalCondition(visible as LogicalCondition);
      } else {
        setConditionType('simple');
        setSimpleCondition(visible as FieldCondition);
      }
    }
  }, [section]);

  const questionOptions = useMemo(
    () =>
      Object.values(questions).map(question => ({
        value: question.questionRef,
        label: question.questionRef,
        selectedDisplay: question.questionRef,
        optionDisplay: (
          <div className="flex w-64 flex-col gap-1">
            <span className="text-sm font-medium text-gray-950">{question.questionRef}</span>
            <span className="text-foreground truncate text-xs">{question.title}</span>
            {questionSectionMap[question.questionRef] && (
              <span className="text-primary truncate text-xs">
                {questionSectionMap[question.questionRef]}
              </span>
            )}
          </div>
        ),
      })),
    [questions, questionSectionMap],
  );

  const selectedQuestion = questions[simpleCondition.field];
  const showValueInput = !['empty', 'notEmpty'].includes(simpleCondition.operator);

  const handleSave = useCallback(() => {
    let conditional: ConditionalBehavior | undefined;

    if (hasCondition) {
      if (conditionType === 'simple' && simpleCondition.field) {
        conditional = { visible: simpleCondition };
      } else if (conditionType === 'logical' && logicalCondition.conditions.length > 0) {
        conditional = { visible: logicalCondition };
      }
    }

    onSave({ title, conditional });
    onClose();
  }, [hasCondition, conditionType, simpleCondition, logicalCondition, title, onSave, onClose]);

  const handleAddLogicalCondition = useCallback(() => {
    setLogicalCondition(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { field: '', operator: 'equals', value: '' } as FieldCondition,
      ],
    }));
  }, []);

  const handleUpdateLogicalCondition = useCallback(
    (index: number, updates: Partial<FieldCondition>) => {
      setLogicalCondition(prev => ({
        ...prev,
        conditions: prev.conditions.map((condition, conditionIndex) =>
          conditionIndex === index ? { ...condition, ...updates } : condition,
        ) as (FieldCondition | LogicalCondition)[],
      }));
    },
    [],
  );

  const handleRemoveLogicalCondition = useCallback((index: number) => {
    setLogicalCondition(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, conditionIndex) => conditionIndex !== index),
    }));
  }, []);

  return {
    title,
    setTitle,
    hasCondition,
    setHasCondition,
    conditionType,
    setConditionType,
    simpleCondition,
    setSimpleCondition,
    logicalCondition,
    setLogicalCondition,
    selectedQuestion,
    showValueInput,
    questionOptions,
    comparisonOperators: COMPARISON_OPERATORS,
    handleSave,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
  };
}
